import { useEffect, useState } from 'react';
import {
  applyLoungeThemeClasses,
  DARK_MODE_CHANGE_EVENT,
  isLoungeDarkModeEnabled,
  isModernLoungeEnabled,
  MODERN_STORAGE_KEY,
  setLoungeDarkModeEnabled,
  syncInAppBodyClass,
} from '../utils/loungeTheme';
import { getDeployVersionLabel } from '../utils/deployVersion';

export const shouldHideModernThemeBanner = (pathname: string) =>
  /^\/disneyland\/user\/[^/]+\/mypost\/?$/.test(pathname);

/**
 * Floating toggle for the modern Disneyland lounge redesign.
 *
 * - Modern design is now the DEFAULT. Layout shells apply the
 *   `mw-lounge-modern` class scope and every Disneyland page gets the
 *   new visual theme out of the box, so search engines and first-time
 *   visitors see the refreshed UI immediately.
 * - Users can opt out via the floating pill (bottom-left). The opt-out
 *   persists as `mw_modern_lounge = "off"` in localStorage.
 * - Any other value (including missing) means modern is on.
 */
export const ModernThemeBanner = () => {
  // Default to enabled so the first paint matches what the user will see.
  const [enabled, setEnabled] = useState(true);
  const [darkEnabled, setDarkEnabled] = useState(false);
  const [bootVersionLabel, setBootVersionLabel] = useState<string | null>(null);
  const hiddenForRoute = shouldHideModernThemeBanner(window.location.pathname);

  // Fetch the currently-loaded build's version stamp on mount so we can
  // surface it inside the toggle pill. Helps figure out which deploy a
  // user is sitting on without DevTools.
  useEffect(() => {
    fetch('/version.txt', { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : null))
      .then((t) => {
        if (!t) return;
        try {
          const j = JSON.parse(t);
          setBootVersionLabel(getDeployVersionLabel(j));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Modern is on by default. Only an explicit "off" flips it back to classic.
    const isOn = isModernLoungeEnabled();
    const isDarkOn = isLoungeDarkModeEnabled();
    setEnabled(isOn);
    setDarkEnabled(isOn && isDarkOn);
    // Body-level class so styles for body-portaled UI (react-modal,
    // react-burger-menu) can scope themselves under `body.mw-modern-mode`
    // and remain inert when the user has opted out.
    applyLoungeThemeClasses(isOn, isDarkOn);
    syncInAppBodyClass();

    const handleDarkModeChange = (event: Event) => {
      const nextEnabled =
        event instanceof CustomEvent
          ? Boolean(event.detail?.enabled)
          : isLoungeDarkModeEnabled();
      setDarkEnabled(nextEnabled);
    };

    window.addEventListener(DARK_MODE_CHANGE_EVENT, handleDarkModeChange);
    return () => {
      window.removeEventListener(DARK_MODE_CHANGE_EVENT, handleDarkModeChange);
    };
  }, []);

  const toggle = () => {
    try {
      if (enabled) {
        // Opt out → persist so the choice survives reloads.
        localStorage.setItem(MODERN_STORAGE_KEY, 'off');
      } else {
        // Back to default (modern). Clear the opt-out flag entirely.
        localStorage.removeItem(MODERN_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  if (hiddenForRoute) {
    return null;
  }

  return (
    <div className='mw-theme-controls'>
      <button
        type='button'
        onClick={toggle}
        className={`mw-theme-toggle${enabled ? ' mw-theme-toggle--on' : ''}`}
        aria-label={
          enabled
            ? 'Switch back to the classic lounge design'
            : 'Try the new lounge design'
        }
      >
        <span className='mw-theme-toggle__icon' aria-hidden='true'>
          {enabled ? '↺' : '✨'}
        </span>
        <span className='mw-theme-toggle__label'>
          {enabled ? 'Back to classic' : 'Try the new lounge'}
          {bootVersionLabel && (
            <span className='mw-theme-toggle__version'>
              v{bootVersionLabel}
            </span>
          )}
        </span>
      </button>
      {enabled && (
        <button
          type='button'
          onClick={() => setDarkEnabled(setLoungeDarkModeEnabled(!darkEnabled))}
          className={`mw-theme-toggle mw-theme-toggle--dark${
            darkEnabled ? ' mw-theme-toggle--dark-on' : ''
          }`}
          aria-label={
            darkEnabled
              ? 'Switch the modern lounge to light mode'
              : 'Switch the modern lounge to dark mode'
          }
        >
          <span className='mw-theme-toggle__icon' aria-hidden='true'>
            {darkEnabled ? '☀' : '☾'}
          </span>
          <span className='mw-theme-toggle__label'>
            {darkEnabled ? 'Light' : 'Dark'}
          </span>
        </button>
      )}
    </div>
  );
};

export default ModernThemeBanner;
